import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransactionsTab({ adminToken }) {
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [transactions, setTransactions] = useState([]);

  const fetch = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/transactions`, { headers: { Authorization: `Bearer ${adminToken}` } }); setTransactions(res.data.transactions || []); }
    catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [adminToken]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleApprove = async (txId) => {
    if (!window.confirm("Approve this payment?")) return;
    try { await axios.post(`${API}/admin/payments/${txId}/approve`, {}, { headers: getHeaders() }); toast.success("Payment approved"); fetch(); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed"); }
  };

  const handleDelete = async (txId) => {
    if (!window.confirm("Permanently delete this transaction record?")) return;
    try { await axios.delete(`${API}/admin/transactions/${txId}`, { headers: getHeaders() }); toast.success("Transaction deleted"); fetch(); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed"); }
  };

  return (
    <Card className="bg-white border-[#E5E7EB]">
      <CardHeader><CardTitle className="text-lg text-[#111827]">All Transactions ({transactions.length})</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
              <TableHead className="text-[#6B7280]">Email</TableHead>
              <TableHead className="text-[#6B7280]">Package</TableHead>
              <TableHead className="text-center text-[#6B7280]">Amount</TableHead>
              <TableHead className="text-center text-[#6B7280]">Status</TableHead>
              <TableHead className="text-[#6B7280]">Date</TableHead>
              <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length > 0 ? transactions.map((tx, i) => (
              <TableRow key={tx.id || i} className="border-[#E5E7EB]">
                <TableCell className="text-[#6B7280]">{tx.user_email}</TableCell>
                <TableCell className="text-[#111827]">{tx.package_id}</TableCell>
                <TableCell className="text-center font-medium text-[#111827]">${(tx.amount || 0).toFixed(2)}</TableCell>
                <TableCell className="text-center"><Badge className={tx.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : tx.payment_status === "pending" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}>{tx.payment_status}</Badge></TableCell>
                <TableCell className="text-[#6B7280]">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {tx.payment_status !== "paid" && <Button variant="ghost" size="sm" onClick={() => handleApprove(tx.id)} className="text-xs text-emerald-500"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>}
                    {tx.approved_by === "admin" && <Badge className="bg-purple-50 text-purple-600 text-[10px]">Admin</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(tx.id)} className="text-xs text-red-500" data-testid={`tx-delete-${tx.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#9CA3AF]">No transactions yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
